import { useState, useRef, useCallback, useEffect } from 'react'
import { PermissionState } from './useMicrophonePermission'

interface UseRecordingStateOptions {
  onRecordingComplete?: (audioUrl: string) => void
}

interface UseRecordingStateReturn {
  // 状態
  isRecording: boolean
  audioURL: string | null
  isAutoStopped: boolean
  isCancelled: boolean
  audioRef: React.RefObject<HTMLAudioElement>

  // 操作
  startRecording: () => Promise<void>
  stopRecording: () => void
  cancelRecording: () => void
  clearAudio: () => void
  setIsAutoStopped: (value: boolean) => void
}

/**
 * 録音状態を管理するフック
 *
 * @param mediaRecorder - MediaRecorderインスタンス
 * @param permissionStatus - マイク権限状態
 * @param requestMicrophoneAccess - マイク権限リクエスト関数
 * @param options - オプション設定
 */
export const useRecordingState = (
  mediaRecorder: MediaRecorder | null,
  permissionStatus: PermissionState,
  requestMicrophoneAccess: () => Promise<MediaRecorder | null>,
  options: UseRecordingStateOptions = {}
): UseRecordingStateReturn => {
  const { onRecordingComplete } = options

  const [isRecording, setIsRecording] = useState(false)
  const [audioURL, setAudioURL] = useState<string | null>(null)
  const [isAutoStopped, setIsAutoStopped] = useState(false)
  const [isCancelled, setIsCancelled] = useState(false)

  const audioRef = useRef<HTMLAudioElement>(null)
  // mediaRecorderへの参照を保持(引数を直接変更しないため)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)

  // mediaRecorderが変更されたらrefを更新
  useEffect(() => {
    mediaRecorderRef.current = mediaRecorder
  }, [mediaRecorder])

  // 録音開始
  const startRecording = useCallback(async () => {
    // 状態リセット
    setIsAutoStopped(false)
    setIsCancelled(false)

    // 既存のURLを解放
    if (audioURL) {
      URL.revokeObjectURL(audioURL)
      setAudioURL(null)
    }

    // 毎回新しいMediaRecorderを取得（streamはactiveなら再利用される）
    const recorder = await requestMicrophoneAccess()
    if (!recorder) return
    mediaRecorderRef.current = recorder

    // 録音開始
    if (recorder.state === 'inactive') {
      recorder.start()
      setIsRecording(true)

      const audioChunks: BlobPart[] = []
      recorder.ondataavailable = (event: BlobEvent) => {
        audioChunks.push(event.data)
      }

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/mp3' })
        const url = URL.createObjectURL(audioBlob)
        setAudioURL(url)
        setIsRecording(false)
        onRecordingComplete?.(url)
      }
    }
  }, [audioURL, requestMicrophoneAccess, onRecordingComplete])

  // 録音停止
  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state === 'recording') {
      recorder.stop()
    }
  }, [])

  // 録音キャンセル
  const cancelRecording = useCallback(() => {
    setIsCancelled(true)

    const recorder = mediaRecorderRef.current
    if (isRecording && recorder && recorder.state === 'recording') {
      // onstopイベントを一時的に無効化
      recorder.onstop = () => {
        setIsRecording(false)
      }
      recorder.stop()
    }

    // 録音データを破棄
    if (audioURL) {
      URL.revokeObjectURL(audioURL)
    }
    setAudioURL(null)
    setIsRecording(false)
    setIsAutoStopped(false)
  }, [isRecording, audioURL])

  // オーディオクリア
  const clearAudio = useCallback(() => {
    if (audioURL) {
      URL.revokeObjectURL(audioURL)
    }
    setAudioURL(null)
    setIsAutoStopped(false)
  }, [audioURL])

  return {
    isRecording,
    audioURL,
    isAutoStopped,
    isCancelled,
    audioRef,
    startRecording,
    stopRecording,
    cancelRecording,
    clearAudio,
    setIsAutoStopped,
  }
}
